import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Divider, Stack } from '@mui/material'
import type { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import {
  PrimaryButton,
  SecondaryButton,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { getReceptorTemplate } from 'src/xgenerated'
import {
  blockIfReceptorNeedsCredentials,
  credentialsActionForPickerSource,
} from 'src/pages/Receptors/folderScanReceptor.helpers'
import {
  useAccountReceptors,
  useInvalidateReceptorsCache,
} from 'src/components/async/model/receptor/useReceptors'
import {
  documentSourceLabel,
  hasSeveralConnections,
} from 'src/pages/Receptors/documentSourceLabel.helpers'
import { useDocumentPicker } from './DocumentPickerContext'
import { documentSourceSpec } from './DocumentPicker.providers'
import { ConnectionSelect } from './DocumentPicker.components'
import { DocumentPickerBrowser } from './DocumentPickerBrowser'
import { DocumentNode, DocumentPickerSource } from './DocumentPicker.types'

const parentModalFor = (
  source: DocumentPickerSource,
  subjectId?: string,
): ModalFormId => {
  switch (source) {
    case 'FolderScan':
      return ModalFormId.ADD_FOLDER_SCAN
    case 'Policy':
      return ModalFormId.ADD_DOCUMENT
    default:
      return subjectId
        ? ModalFormId.ADD_EVIDENCE
        : ModalFormId.ADD_EVIDENCE_GROUPS
  }
}

/**
 * The document picker. One modal for every document receptor: it browses the
 * receptor's repository through ntrced, one level at a time, under the
 * receptor's own identity, and hands the picked nodes back through
 * DocumentPickerContext.
 */
export const DocumentPickerModal = (): JSX.Element | null => {
  const { request, setSelection } = useDocumentPicker()
  const isVisible = useIsShowModal(ModalFormId.DOCUMENT_PICKER)
  const hide = useHideModal({ modalId: ModalFormId.DOCUMENT_PICKER })

  // The connection being browsed: the one the request named, until the
  // person switches to another connection of the same model in the header.
  const [switchedReceptor, setSwitchedReceptor] = useState<
    ReceptorRecord | undefined
  >(undefined)
  const receptor = switchedReceptor ?? request?.receptor
  const receptorId = receptor?.getId()
  const modelId = receptor?.getModelid() || ''
  const spec = useMemo(() => documentSourceSpec(modelId), [modelId])

  const source: DocumentPickerSource = request?.source || 'Evidence'
  const mode = request?.mode || 'files'
  const foldersMode = mode === 'folders'
  const parentModal =
    request?.parentModalId || parentModalFor(source, request?.subjectId)
  const showParentModal = useSetActiveModal(parentModal)
  const invalidateReceptors = useInvalidateReceptorsCache()

  const [selectedNodes, setSelectedNodes] = useState<DocumentNode[]>([])

  // Each new request starts from the connection it names with nothing picked.
  useEffect(() => {
    setSwitchedReceptor(undefined)
    setSelectedNodes([])
  }, [request])

  // A receptor with missing or rejected credentials cannot browse. Close with
  // the reason instead of listing into an error.
  useEffect(() => {
    if (!isVisible || !receptor) return
    if (
      blockIfReceptorNeedsCredentials(
        receptor,
        receptor.getName() || getReceptorTemplate(receptor.getModelid()).name,
        credentialsActionForPickerSource(source),
      )
    ) {
      hide()
      if (source !== 'FolderScan') showParentModal()
    }
  }, [isVisible, receptor, source, hide, showParentModal])

  // With several connections of this model the title names the one browsed;
  // with one, the title is the vendor's alone, as before.
  const { data: accountReceptors } = useAccountReceptors()
  const severalConnections = useMemo(
    () =>
      !request?.lockConnection &&
      hasSeveralConnections(accountReceptors?.getReceptorsList(), modelId),
    [accountReceptors, modelId, request],
  )

  // Every enabled connection of this model, offered in the header when
  // there is more than one. The requested row is listed even if disabled,
  // so the select always has a current value.
  const connections = useMemo<ReceptorRecord[]>(() => {
    const requested = request?.receptor
    const enabled = (accountReceptors?.getReceptorsList() || []).filter(
      (r) => r.getModelid() === modelId && r.getIsenabled(),
    )
    if (requested && !enabled.some((r) => r.getId() === requested.getId())) {
      return [requested, ...enabled]
    }
    return enabled
  }, [accountReceptors, modelId, request])

  const switchConnection = (next: ReceptorRecord): void => {
    if (next.getId() === receptorId) return
    if (
      blockIfReceptorNeedsCredentials(
        next,
        documentSourceLabel(next),
        credentialsActionForPickerSource(source),
      )
    ) {
      return
    }
    setSwitchedReceptor(next)
    setSelectedNodes([])
  }

  const handleCancel = (): void => {
    hide()
    if (source !== 'FolderScan') showParentModal()
  }

  const handleAdd = (): void => {
    if (!receptor) return
    if (selectedNodes.length === 0) {
      showInfoToast(
        foldersMode
          ? `Select a ${spec.label} folder first`
          : 'Select at least one document',
      )
      return
    }
    if (foldersMode && selectedNodes.length !== 1) {
      showInfoToast(`Select one ${spec.label} folder`)
      return
    }
    setSelection({ receptor, source, nodes: selectedNodes })
    hide()
    showParentModal()
  }

  const handleCredentialsError = useCallback(() => {
    if (!receptor) return
    invalidateReceptors()
    showInfoToast(
      `${
        receptor.getName() || getReceptorTemplate(receptor.getModelid()).name
      } credentials are invalid or expired. Re-authorize it with "Edit credentials" on the receptor before ${credentialsActionForPickerSource(
        source,
      )}.`,
    )
    hide()
    if (source !== 'FolderScan') showParentModal()
  }, [receptor, source, invalidateReceptors, hide, showParentModal])

  if (!request || !receptor) return null

  const baseTitle = foldersMode
    ? `Choose a ${spec.label} folder`
    : `Add ${spec.label} ${source === 'Policy' ? 'documents' : 'evidence'}`
  const title = severalConnections ? (
    <>
      {baseTitle} from
      <ConnectionSelect
        receptors={connections}
        value={receptorId || ''}
        onChange={switchConnection}
      />
    </>
  ) : (
    baseTitle
  )

  return (
    <ModalForm
      hide={handleCancel}
      show={isVisible}
      formId={ModalFormId.DOCUMENT_PICKER}
      title={title}
      backdrop="static"
      hideButtons
    >
      <Stack spacing={1}>
        <DocumentPickerBrowser
          receptor={receptor}
          mode={mode}
          source={source}
          onSelectionChange={setSelectedNodes}
          onCredentialsError={handleCredentialsError}
        />

        <Divider sx={{ my: 1 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <SecondaryButton
            text="Cancel"
            onClick={handleCancel}
            size={StandardButtonSize.SMALL}
          />
          <PrimaryButton
            text="Add"
            onClick={handleAdd}
            size={StandardButtonSize.SMALL}
          />
        </Stack>
      </Stack>
    </ModalForm>
  )
}
